import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react"

import { cn } from "../../../lib/utils"
import { ToggleGroup, ToggleGroupItem } from "../toggle-group/toggle-group"

const SEGMENTED_GROUP_DEFAULT = "bg-muted w-fit gap-1 rounded-md p-0.5"
const SEGMENTED_GROUP_SUBTLE =
  "border-border bg-muted/50 w-fit gap-1 rounded-md border p-0.5"
const SEGMENTED_ITEM =
  "text-muted-foreground relative z-[1] h-7 rounded-sm bg-transparent px-3 text-xs shadow-none transition-[color,transform] duration-200 ease-out hover:text-foreground data-[state=on]:bg-transparent data-[state=on]:shadow-none active:scale-[0.98] disabled:hover:text-muted-foreground motion-reduce:transition-none motion-reduce:active:scale-100"
// Slides on `transform` rather than `left` so the move is composited. `width`
// has to animate too — the segments are different sizes — but the indicator is
// positioned out of flow, so resizing it reflows nothing around it.
const SEGMENTED_INDICATOR =
  "pointer-events-none absolute top-0.5 bottom-0.5 left-0 z-0 rounded-sm transition-[transform,width] duration-200 ease-out motion-reduce:transition-none"

// A raised white pill reads as "this one is on top of the others", which suits
// a filter refining what is already on screen. A tinted fill reads as "this one
// is chosen", which is what a control that swaps the panel underneath it needs.
// `primary-subtle` rather than `bg-primary/10`: the token already carries the
// heavier dark-mode value that a fixed opacity would have to hardcode.
const SEGMENTED_INDICATOR_ACCENT = {
  surface: "bg-background shadow-sm",
  primary: "bg-primary-subtle",
} as const

/** Paired with the indicator fill above, so the label stays legible on it. */
const SEGMENTED_ITEM_ACCENT = {
  surface: "data-[state=on]:text-foreground",
  primary: "data-[state=on]:text-primary-subtle-foreground",
} as const

interface Indicator {
  left: number
  width: number
}

export interface SegmentedOption {
  value: string
  label: string
  /** Decorative prefix inside the segment (status dot, icon). */
  start?: ReactNode
  /** Offered but not selectable — an option with nothing behind it. */
  disabled?: boolean
  /** Native tooltip, for a label that abbreviates something longer. */
  title?: string
}

export interface SegmentedControlProps {
  value: string
  onValueChange: (value: string) => void
  options: SegmentedOption[]
  disabled?: boolean
  ariaLabel: string
  /** Stretch to container width on small screens; segments share space equally. */
  fill?: boolean
  /** Lighter track with border — used in dense table toolbars. */
  tone?: "default" | "subtle"
  /**
   * Colour of the selection pill. `surface` is the neutral default that suits a
   * filter; `primary` tints it for a control that switches what is displayed.
   */
  accent?: "surface" | "primary"
  className?: string
}

/**
 * One choice out of a handful, all of them on screen. A sliding pill carries the
 * selection from one segment to the next, so the change reads as a move rather
 * than as two separate things switching off and on.
 */
export function SegmentedControl({
  value,
  onValueChange,
  options,
  disabled = false,
  ariaLabel,
  fill = false,
  tone = "default",
  accent = "surface",
  className,
}: SegmentedControlProps) {
  const groupRef = useRef<HTMLDivElement>(null)
  // Null until measured. The pill is only rendered once it has somewhere to be,
  // because a freshly inserted element does not transition from its first
  // style — which is what keeps it from sliding in from the left on mount.
  const [indicator, setIndicator] = useState<Indicator | null>(null)

  const syncIndicator = useCallback(() => {
    const group = groupRef.current
    if (!group || !value) {
      setIndicator(null)
      return
    }

    const active = group.querySelector<HTMLElement>(
      '[data-slot="toggle-group-item"][data-state="on"]',
    )
    if (!active) {
      setIndicator(null)
      return
    }

    setIndicator({ left: active.offsetLeft, width: active.offsetWidth })
  }, [value])

  useLayoutEffect(() => {
    syncIndicator()
  }, [syncIndicator, options])

  useEffect(() => {
    const group = groupRef.current
    if (!group) return

    const observer = new ResizeObserver(syncIndicator)
    observer.observe(group)
    return () => observer.disconnect()
  }, [syncIndicator])

  return (
    <div
      ref={groupRef}
      className={cn("relative", fill ? "w-full md:w-fit" : "w-fit", className)}
    >
      {indicator ? (
        <div
          className={cn(SEGMENTED_INDICATOR, SEGMENTED_INDICATOR_ACCENT[accent])}
          style={{
            width: indicator.width,
            transform: `translateX(${indicator.left}px)`,
          }}
          aria-hidden
        />
      ) : null}
      <ToggleGroup
        type="single"
        variant="default"
        value={value}
        // Radix reports "" when the pressed segment is toggled off, which would
        // leave the control with no answer at all.
        onValueChange={(next) => {
          if (next) onValueChange(next)
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(
          tone === "subtle" ? SEGMENTED_GROUP_SUBTLE : SEGMENTED_GROUP_DEFAULT,
          fill && "w-full md:w-fit",
        )}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            title={option.title}
            className={cn(
              SEGMENTED_ITEM,
              SEGMENTED_ITEM_ACCENT[accent],
              option.start && "gap-1.5",
              fill && "min-w-0 flex-1 justify-center md:flex-none",
            )}
          >
            {option.start}
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  )
}
